/**
 * Report Execution Hooks
 *
 * React hooks for executing reports and retrieving results.
 */

import { useMutation } from '@tanstack/react-query';
import { executeReport, ReportConfiguration, ReportResults } from '../services/reportEngine';
import { getRecordsForObject as getRecordsFromStorage } from '../services/recordStorageService';
import { shouldUseGobodo } from '../utils/gobodoUtils';
import { normalizeRecordForListView } from '../utils/listViewRecordNormalizer';
import { persistedLeadRecords } from '../services/mockData/leads';
import { persistedClientRecords } from '../services/mockData/clients';
import { persistedContactRecords } from '../services/mockData/contacts';
import { persistedOpportunityRecords } from '../services/mockData/opportunities';
import { persistedCaseRecords } from '../services/mockData/cases';
import { persistedEventRecords } from '../services/mockData/events';
import { persistedTaskRecords } from '../services/mockData/tasks';
import { persistedReportRecords } from '../services/mockData/reports';

const GOBODO_OBJECT_NAMES = new Set(['Lead', 'Client', 'Contact', 'Opportunity', 'Case', 'Event', 'Task', 'Report']);

/**
 * Hook to execute a report
 */
export function useExecuteReport() {
  return useMutation({
    mutationFn: async (config: ReportConfiguration): Promise<ReportResults> => {
      const records = await getRecordsForReportObject(config.objectName);
      const normalized = records.map(r => normalizeRecordForListView(config.objectName, r));
      return executeReport(config, normalized);
    }
  });
}

export async function getRecordsForReportObject(objectName: string) {
  if (shouldUseGobodo() && GOBODO_OBJECT_NAMES.has(objectName)) {
    const records = await getRecordsFromStorage(objectName);
    return records;
  }
  switch (objectName) {
    case 'Lead':
      return persistedLeadRecords.get();
    case 'Client':
      return persistedClientRecords.get();
    case 'Contact':
      return persistedContactRecords.get();
    case 'Opportunity':
      return persistedOpportunityRecords.get();
    case 'Case':
      return persistedCaseRecords.get();
    case 'Event':
      return persistedEventRecords.get();
    case 'Task':
      return persistedTaskRecords.get();
    case 'Report':
      return persistedReportRecords.get();
    default:
      return [];
  }
}
